import type { CallbackEvent } from "@shopify/polaris-types";
import {
  CollapsibleSectionCard,
  SectionHeader,
  ProductPickerButton,
  ColorRow,
  NumericField,
} from "../../../components/shared/EditorShared";
import { openTriggerProductsPicker } from "../../../utils/resource-picker.utils";
import type {
  DownsellEditorState,
  DiscountAction,
} from "../../../store/downsell-editor.mapping";

// ─── Types ───────────────────────────────────────────────────────────────────

export type DownsellTriggerType = "all" | "specific_products";
export type BackgroundImagePreset =
  | "preset_1"
  | "preset_2"
  | "preset_3"
  | "none";

// ─── Background image presets ─────────────────────────────────────────────────

export const BACKGROUND_PRESETS: {
  id: BackgroundImagePreset;
  label: string;
  gradient: string;
}[] = [
  {
    id: "preset_1",
    label: "Rojo/Naranja",
    gradient: "linear-gradient(135deg, #c0392b 0%, #e74c3c 40%, #e67e22 100%)",
  },
  {
    id: "preset_2",
    label: "Azul",
    gradient: "linear-gradient(135deg, #1a6a9a 0%, #2980b9 50%, #3498db 100%)",
  },
  {
    id: "preset_3",
    label: "Gris/Nube",
    gradient: "linear-gradient(135deg, #95a5a6 0%, #bdc3c7 50%, #ecf0f1 100%)",
  },
];

// ─── NameSection ─────────────────────────────────────────────────────────────

export function NameSection({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={1} title="Nombre del downsell" />}
    >
      <s-stack direction="block" gap="base">
        <s-text-field
          label="Nombre interno"
          value={value}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onChange(e.currentTarget.value)
          }
        />
        <s-text color="subdued">
          Este nombre es solo para ti, tus clientes no lo verán.
        </s-text>
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 1: Configurar el downsell ───────────────────────────────────────

const TRIGGER_OPTIONS: { value: DownsellTriggerType; label: string }[] = [
  { value: "all", label: "Todos los productos" },
  { value: "specific_products", label: "Productos específicos" },
];

const TIMES_SHOWN_OPTIONS = [1, 2, 3, 5, 10];

const DISCOUNT_TYPE_OPTIONS: { value: DiscountAction; label: string }[] = [
  { value: "percentage_off", label: "Porcentaje" },
  { value: "fixed_amount_off", label: "Monto fijo" },
];

export function ConfigSection({
  values,
  onConfigChange,
}: {
  values: Pick<
    DownsellEditorState,
    | "triggerType"
    | "triggerProducts"
    | "timesShownLimit"
    | "backgroundImagePreset"
    | "backgroundColor"
    | "borderRadius"
    | "borderWidth"
    | "borderColor"
    | "pricingAction"
    | "pricingValue"
  >;
  onConfigChange: <K extends keyof DownsellEditorState>(
    key: K,
    value: DownsellEditorState[K]
  ) => void;
}) {
  const {
    triggerType,
    triggerProducts,
    timesShownLimit,
    backgroundImagePreset,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    pricingAction,
    pricingValue,
  } = values;

  const removeProduct = (id: string) =>
    onConfigChange(
      "triggerProducts",
      triggerProducts.filter((p) => p.id !== id)
    );

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={2} title="Configurar el downsell" />}
    >
      <s-stack direction="block" gap="base">
        {/* Name field is handled by the editor directly */}

        {/* Trigger toggle */}
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Mostrar el downsell para:</s-text>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            {TRIGGER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onConfigChange("triggerType", opt.value)}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                  triggerType === opt.value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {triggerType === "specific_products" && (
            <s-stack direction="block" gap="small-200">
              <ProductPickerButton
                label={
                  triggerProducts.length > 0
                    ? `${triggerProducts.length} producto(s) seleccionado(s)`
                    : "Seleccionar productos"
                }
                onClick={() =>
                  openTriggerProductsPicker(
                    triggerProducts,
                    (prods) => onConfigChange("triggerProducts", prods),
                    () =>
                      window.shopify?.toast?.show(
                        "No se pudo abrir el selector de productos.",
                        { isError: true }
                      )
                  )
                }
              />
              {triggerProducts.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
                >
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.title}
                      className="w-10 h-10 rounded object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <s-icon type="image" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {p.title}
                    </p>
                  </div>
                  <button
                    onClick={() => removeProduct(p.id)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                    aria-label="Quitar producto"
                  >
                    <s-icon type="delete" />
                  </button>
                </div>
              ))}
            </s-stack>
          )}
        </s-stack>

        {/* Times shown */}
        <s-stack direction="block" gap="small-200">
          <s-text>
            El downsell se mostrará cuando el cliente cierre el formulario:
          </s-text>
          <s-select
            value={String(timesShownLimit)}
            onChange={(e: CallbackEvent<"s-select">) =>
              onConfigChange("timesShownLimit", Number(e.currentTarget.value))
            }
          >
            {TIMES_SHOWN_OPTIONS.map((n) => (
              <s-option key={n} value={String(n)}>
                {n} {n === 1 ? "vez" : "veces"}
              </s-option>
            ))}
          </s-select>
        </s-stack>

        {/* Background image presets */}
        <s-stack direction="block" gap="small-200">
          <s-text>Cambiar imagen de fondo:</s-text>
          <div className="flex gap-3 flex-wrap">
            {BACKGROUND_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() =>
                  onConfigChange("backgroundImagePreset", preset.id)
                }
                className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  backgroundImagePreset === preset.id
                    ? "border-indigo-600 shadow-md"
                    : "border-gray-200 hover:border-gray-400"
                }`}
                title={preset.label}
                style={{ background: preset.gradient }}
              >
                {backgroundImagePreset === preset.id && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <span className="text-white text-lg font-bold">✓</span>
                  </div>
                )}
              </button>
            ))}
            {/* No image option */}
            <button
              onClick={() => onConfigChange("backgroundImagePreset", "none")}
              className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 transition-all bg-gray-100 flex items-center justify-center ${
                backgroundImagePreset === "none"
                  ? "border-indigo-600 shadow-md"
                  : "border-gray-200 hover:border-gray-400"
              }`}
              title="Sin imagen"
            >
              <s-icon type="image-none" />
            </button>
          </div>
        </s-stack>

        {/* Background color */}
        <ColorRow
          label="O elige el color de fondo"
          value={backgroundColor}
          onChange={(v) => onConfigChange("backgroundColor", v)}
        />

        {/* Border radius + width */}
        <div className="grid grid-cols-2 gap-3">
          <s-stack direction="block" gap="small-200">
            <s-text>Radio del borde</s-text>
            <input
              type="range"
              min={0}
              max={50}
              value={borderRadius}
              onChange={(e) =>
                onConfigChange("borderRadius", Number(e.target.value))
              }
              className="w-full accent-indigo-600"
            />
          </s-stack>
          <s-stack direction="block" gap="small-200">
            <s-text>Ancho del borde</s-text>
            <input
              type="range"
              min={0}
              max={10}
              value={borderWidth}
              onChange={(e) =>
                onConfigChange("borderWidth", Number(e.target.value))
              }
              className="w-full accent-indigo-600"
            />
          </s-stack>
        </div>

        {/* Border color */}
        <ColorRow
          label="Color del borde"
          value={borderColor}
          onChange={(v) => onConfigChange("borderColor", v)}
        />

        {/* Discount type + value */}
        <div className="grid grid-cols-2 gap-3">
          <s-select
            label="Tipo de descuento:"
            value={pricingAction}
            onChange={(e: CallbackEvent<"s-select">) =>
              onConfigChange(
                "pricingAction",
                e.currentTarget.value as DiscountAction
              )
            }
          >
            {DISCOUNT_TYPE_OPTIONS.map((opt) => (
              <s-option key={opt.value} value={opt.value}>
                {opt.label}
              </s-option>
            ))}
          </s-select>
          <NumericField
            label="Valor del descuento"
            min={0}
            value={pricingValue}
            onChange={(v) => onConfigChange("pricingValue", v)}
          />
        </div>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
